import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { type SkillSummary } from "@/shared/domain/skills";
import { useSkills } from "@/shared/domain/skills/hooks";

import { eligibleAgentSkills } from "./agentSkills";

export interface AgentEligibleSkills {
  /** The query as it stands, so each caller reads the state it cares about. */
  query: ReturnType<typeof useSkills>;
  /** The org-shared skills an agent may attach. Empty until the query settles. */
  skills: SkillSummary[];
  /** The same by id, or absent while the list is still on its way. */
  ids: ReadonlySet<string> | undefined;
}

/**
 * The skills this org offers an agent.
 *
 * No scope filter: the request returns every visible skill, which is then
 * narrowed to the org-shared tiers here. The "org" scope would wrongly leave
 * out org-shared skills the author wrote themselves.
 */
export function useAgentEligibleSkills(): AgentEligibleSkills {
  const orgId = useCurrentOrgId();
  const query = useSkills(orgId);
  const items = query.data?.items;
  const skills = React.useMemo(() => eligibleAgentSkills(items ?? []), [items]);
  const settled = query.isSuccess;
  const ids = React.useMemo(
    () =>
      settled
        ? new Set(skills.map((summary) => summary.skill.skill_id))
        : undefined,
    [settled, skills],
  );

  return { query, skills, ids };
}
